/**
 * ChannelRoiListPage — Issue #137 + Issue #345.
 *
 * Backed by the existing `useChannelPartnerList` + `useTransitionCooperationStatus`
 * hooks (real backend endpoints). Issue #345 wraps the previous hand-coded
 * table in the shared `AdminDataTable` so the page gains advanced search,
 * column visibility, loading/empty/error states, and a permission-aware row
 * action menu that drives the cooperation state-machine through the existing
 * POST /channel-roi/partners/{partnerId}/cooperation-status/transition endpoint.
 *
 * Channel cooperation is contractually billed and audit-sensitive, so the
 * destructive action is `归档` (event ARCHIVE) rather than physical deletion.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { toast } from 'sonner';
import { Archive, Eye, PauseCircle, PlayCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { useChannelPartnerList } from '@/hooks/useCrm';
import type { ChannelPartnerDto } from '@/hooks/useCrm';
import {
  COOPERATION_STATUS_LABELS,
  useTransitionCooperationStatus,
  type ChannelPartnerCooperationStatus,
  type TransitionCooperationStatusDto,
} from '@/hooks/useChannelRoi';
import {
  CHANNEL_ROI_PERMISSIONS,
  hasPermission,
} from '@/lib/channelRoiPermissions';
// Issue #345 audit fix2: useChannelPartnerList 仅接受 `{ page, limit, status }`
// 而合作状态 `cooperationStatus` 与渠道名称 `name` 都不在后端筛选 DTO 中。先前
// 的实现拿到一页后再前端过滤 cooperationStatus / name，却把 result.total（按
// 后端筛选计算）直接显示给用户，会让筛选命中和翻页 total 不一致并跳过命中
// 行。在后端补齐 cooperationStatus / name 查询参数前，工具栏不再渲染这两个
// 误导用户的筛选项。
import {
  AdminDataTable,
  type AdminDataTableAction,
  type AdminDataTableColumn,
} from '@/components/business/AdminDataTable';
import { Button } from '@/components/ui/button';

type CooperationEvent = TransitionCooperationStatusDto['event'];

function CoopStatusBadge({ status }: { status?: string | null }) {
  const label =
    status && status in COOPERATION_STATUS_LABELS
      ? COOPERATION_STATUS_LABELS[status as ChannelPartnerCooperationStatus]
      : status ?? '—';

  const colorMap: Readonly<Record<string, string>> = {
    prospecting: 'bg-gray-100 text-gray-700',
    negotiating: 'bg-blue-100 text-blue-700',
    contracted: 'bg-indigo-100 text-indigo-700',
    active_cooperation: 'bg-green-100 text-green-700',
    suspended_cooperation: 'bg-yellow-100 text-yellow-700',
    terminated: 'bg-red-100 text-red-700',
    archived: 'bg-gray-100 text-gray-500',
  };
  const cls = (status && colorMap[status]) ?? 'bg-gray-100 text-gray-700';

  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

export default function ChannelRoiListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { loading, error, fetch } = useChannelPartnerList();
  const { loading: transitioning, transition } = useTransitionCooperationStatus();
  const [partners, setPartners] = useState<readonly ChannelPartnerDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 20;

  const canRead = hasPermission(permissions, CHANNEL_ROI_PERMISSIONS.PARTNER_READ);
  const canUpdate = hasPermission(
    permissions,
    CHANNEL_ROI_PERMISSIONS.PARTNER_UPDATE,
  );

  const loadData = useCallback(async () => {
    if (!canRead) return;
    // Issue #345 audit fix2: 后端 useChannelPartnerList 不支持 cooperationStatus
    // 或名称模糊查询；不要在拿到分页结果后再前端过滤命中条数，否则会与
    // result.total 不一致。
    const result = await fetch({ page, limit });
    if (result) {
      setPartners(result.data);
      setTotal(result.total);
    }
  }, [canRead, fetch, page]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (error) {
      toast.error(`加载渠道列表失败：${error.message}`);
    }
  }, [error]);

  const visibleRows = partners;

  const fireTransition = useCallback(
    async (
      p: ChannelPartnerDto,
      event: CooperationEvent,
      verb: string,
      destructive: boolean,
    ) => {
      const prompt = destructive
        ? `将渠道「${p.name}」${verb}。该操作不会物理删除佣金 / 合作记录，可由超级管理员根据合规流程恢复。继续吗？`
        : `将渠道「${p.name}」${verb}。是否继续？`;
      if (!window.confirm(prompt)) return;
      const ok = await transition(p.id, {
        event,
        expectedVersion: p.version,
      });
      if (ok) {
        toast.success(`渠道「${p.name}」${verb}成功`);
        await loadData();
      }
    },
    [loadData, transition],
  );

  const columns: ReadonlyArray<AdminDataTableColumn<ChannelPartnerDto>> = useMemo(
    () => [
      {
        key: 'name',
        header: '渠道名称',
        alwaysVisible: true,
        render: (p) => <span className="font-medium">{p.name}</span>,
      },
      {
        key: 'cooperationStatus',
        header: '合作状态',
        render: (p) => <CoopStatusBadge status={p.cooperationStatus} />,
      },
      {
        key: 'commissionRate',
        header: '佣金率',
        render: (p) => p.commissionRate ?? '—',
      },
      {
        key: 'cooperationType',
        header: '合作类型',
        defaultHidden: true,
        render: (p) => p.cooperationType ?? '—',
      },
      {
        key: 'contractEndDate',
        header: '合同到期',
        render: (p) =>
          p.contractEndDate
            ? new Date(p.contractEndDate).toLocaleDateString('zh-CN')
            : '—',
      },
    ],
    [],
  );

  const rowActions: ReadonlyArray<AdminDataTableAction<ChannelPartnerDto>> = useMemo(
    () => [
      {
        key: 'view',
        label: '查看详情',
        icon: <Eye className="h-3.5 w-3.5" />,
        onSelect: (p) => navigate(`/channel-roi/partners/${p.id}`),
      },
      {
        key: 'suspend',
        label: '暂停合作',
        icon: <PauseCircle className="h-3.5 w-3.5" />,
        visible: (p) =>
          canUpdate && p.cooperationStatus === 'active_cooperation',
        disabled: () => transitioning,
        onSelect: (p) => void fireTransition(p, 'SUSPEND', '暂停', false),
      },
      {
        key: 'resume',
        label: '恢复合作',
        icon: <PlayCircle className="h-3.5 w-3.5" />,
        visible: (p) =>
          canUpdate && p.cooperationStatus === 'suspended_cooperation',
        disabled: () => transitioning,
        onSelect: (p) => void fireTransition(p, 'RESUME', '恢复', false),
      },
      {
        key: 'archive',
        label: '归档',
        icon: <Archive className="h-3.5 w-3.5" />,
        destructive: true,
        visible: (p) =>
          canUpdate &&
          (p.cooperationStatus === 'terminated' ||
            p.cooperationStatus === 'suspended_cooperation' ||
            p.cooperationStatus === 'prospecting'),
        disabled: () => transitioning,
        onSelect: (p) => void fireTransition(p, 'ARCHIVE', '归档', true),
      },
    ],
    [canUpdate, fireTransition, navigate, transitioning],
  );

  if (!canRead) {
    return <AccessDenied resource="渠道管理" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">渠道管理 · ROI 概览</h2>
        {canUpdate && (
          <Button
            type="button"
            onClick={() => navigate('/crm/channel-partners/create')}
          >
            新建渠道
          </Button>
        )}
      </div>

      <AdminDataTable<ChannelPartnerDto>
        testIdPrefix="channel-roi"
        columns={columns}
        rows={visibleRows}
        isLoading={loading}
        error={error}
        onRetry={loadData}
        emptyMessage="暂无渠道数据"
        pagination={{
          page,
          pageSize: limit,
          total,
          onPageChange: setPage,
        }}
        rowActions={rowActions}
      />
    </div>
  );
}
